import { CloseIcon, CopyIcon, ExclamationCircleIcon, SearchIcon } from '@panwds/icons';
import { Button, List, ListItem, Portal, Tooltip, useOnOutsideClick } from '@panwds/react-ui';
import { classNames } from '@panwds/tailwindcss';
import { map } from 'lodash';
import memoize from 'micro-memoize';
import React, { useEffect, useRef, useState } from 'react';
import { ADVANCED_FILTER_BAR_MAX_CHARATER_LENGTH, MEMOIZE_FIELDS_MAX_SIZE } from '../../utils/constants.js';
import { parse } from '../../utils/queryExpression.js';
import { getSuggestion } from '../../utils/suggestionHelper.js';
import { getStart, isNil, isNilOrEmptyString } from '../../utils/utils.js';
const EMPTY_VALUE = { value: '', name: '' };
const DEFAULT_SUPPORT_NOT = false;
const DEFAULT_SUPPORT_PARENTHESIS = true;
const DEFAULT_DATE_FORMAT = 'MM dd yyyy';
export function LogQuery(props) {
    const [inputValue, setInputValue] = useState(props.queryString);
    const [options, setOptions] = useState([]);
    const [value, setValue] = useState({
        value: props.queryString,
        name: props.queryString
    });
    const [open, setOpen] = useState(false);
    const [errors, setErrors] = useState([]);
    const [fields, setFields] = useState([]);
    const [isSelected, setIsSelected] = useState(false);
    const inputRef = useRef(null);
    const isFirstRun = useRef(true);
    const needOpenSuggestion = useRef(false);
    const dropdownRef = useRef(null);
    const supportNot = props.supportNot || DEFAULT_SUPPORT_NOT;
    const supportParen = props.supportParen ?? DEFAULT_SUPPORT_PARENTHESIS;
    const timeFormat = props.timeFormat || DEFAULT_DATE_FORMAT;
    const maxLength = props.maxLength || ADVANCED_FILTER_BAR_MAX_CHARATER_LENGTH;
    // Creating a cache of the schema fields first sent by the user
    const cache = useRef(memoize((query, props) => {
        return parse(query, props.fields, { supportNot, supportParen }, props.commonFilters);
    }, {
        transformKey(args) {
            return args[0];
        },
        maxSize: MEMOIZE_FIELDS_MAX_SIZE,
        isEqual: (a, b) => a === b
    }));
    // Detects when the click is triggered outside the popover
    useOnOutsideClick([inputRef, dropdownRef], () => {
        setOpen(false);
    });
    useEffect(() => {
        // do not open the suggestions when first time rendered
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (needOpenSuggestion.current) {
            openSuggestions();
        }
        needOpenSuggestion.current = true;
        let cursor;
        if (isNil(value.cursor)) {
            cursor = inputRef.current.selectionStart;
        }
        handleSearch(fields, value.value, cursor);
    }, [value, isSelected]);
    // clear cache when fields are changed
    useEffect(() => {
        cache.current.cache.keys.length = 0;
        cache.current.cache.values.length = 0;
        const newFields = [];
        const newOptions = !isNil(props.fields)
            ? map(props.fields, (field, fieldName) => {
                if (field.type !== 'array') {
                    newFields.push({
                        name: fieldName,
                        type: field.type
                    });
                    return {
                        name: fieldName,
                        value: fieldName,
                        type: field.type,
                        cursor: -1,
                        matched: -1
                    };
                }
                return [];
            })
            : [];
        setOptions(newOptions);
        setFields(newFields);
        // avoid extra search on saved filter load
        if (isNilOrEmptyString(props.savedFilter)) {
            handleSearch(newFields, value.value, inputRef.current.selectionStart);
        }
    }, [props.fields, props.savedFilter]);
    useEffect(() => {
        // Checks if the queryString sent by user is not equal to the current
        // input value, then force sets the value to the one sent by user
        if (props.queryString !== inputValue) {
            setInputValue(props.queryString);
            setValue({ value: props.queryString, name: props.queryString });
            setIsSelected(false);
            needOpenSuggestion.current = false;
        }
        else {
            needOpenSuggestion.current = true;
        }
    }, [props.queryString]);
    const onQueryChange = (queryInput) => {
        props.onQueryChange(queryInput);
    };
    // Triggers the search when the user submits the query
    const handleSearch = (fields, newValue, cursor) => {
        const suggestions = [];
        if (!isNil(newValue) && newValue.length > maxLength) {
            setErrors([new Error('query_too_long')]);
            onQueryChange(newValue);
        }
        else {
            if (fields.length > 0) {
                const queryExp = cache.current(newValue, props);
                const newSuggestions = getSuggestion(fields, supportNot, newValue, timeFormat, cursor, queryExp);
                suggestions.push(...newSuggestions);
                setErrors(queryExp.errors);
                onQueryChange(newValue);
            }
        }
        setOptions(suggestions);
    };
    // Sets the suggestion box to be open
    const openSuggestions = () => {
        setOpen(true);
    };
    // only update input value when it comes from user typing, ignore the event from select option
    const handleInputChange = (event) => {
        const newValue = event.target.value;
        setValue({ value: newValue, name: newValue });
        setInputValue(newValue);
        setIsSelected(false);
        openSuggestions();
    };
    // Closes the suggestion box
    const handleClose = () => {
        setOpen(false);
    };
    // Clears the query when clear icon is clicked
    const handleClickClear = (e) => {
        setValue(EMPTY_VALUE);
        setInputValue('');
        setIsSelected(false);
        handleClose();
        needOpenSuggestion.current = false;
        e.stopPropagation();
    };
    /* istanbul ignore next */
    // Triggered when the user clicks on copy icon
    const handleClickCopy = (e) => {
        inputRef.current.select();
        navigator.clipboard.writeText(inputValue).then(() => {
            /* clipboard successfully set */
        }, () => {
            /* clipboard write failed */
        });
        e.target.focus();
    };
    const handleCursorMayChange = () => {
        const last = inputRef.current.dataset['selectionStart'] ? +inputRef.current.dataset['selectionStart'] : undefined;
        if (inputRef.current.selectionStart && last !== +inputRef.current.selectionStart) {
            inputRef.current.dataset['selectionStart'] = inputRef.current.selectionStart.toString();
            handleSearch(fields, value.value, inputRef.current.selectionStart);
        }
        openSuggestions();
    };
    const onQuerySubmit = () => {
        if (errors.length === 0) {
            props.onQuerySubmit(inputValue);
        }
    };
    const handleOnKeyDown = (event) => {
        if (event.key === 'Enter' && errors.length === 0) {
            event.preventDefault();
            props.onQuerySubmit(inputValue);
            return;
        }
    };
    const onSuggestionClick = (e) => {
        handleClose();
        const newValue = e.currentTarget.textContent;
        const lastCharacter = inputValue.charAt(inputValue.length - 1);
        let newInputValue = inputValue;
        if (lastCharacter === newValue) {
            newInputValue = `${inputValue} `;
        }
        else if (newValue.includes(lastCharacter)) {
            const lastIndex = inputValue.lastIndexOf(' ');
            newInputValue = `${inputValue.substring(0, lastIndex + 1) + newValue} `;
        }
        else if (inputRef.current.selectionStart !== inputValue.length) {
            const selection = inputRef.current.selectionStart;
            const wordStart = getStart(inputValue, selection);
            const beforeSelection = inputValue.substring(0, selection);
            const afterSelection = inputValue.substring(selection);
            if (inputValue.charAt(selection) !== ' ') {
                if (inputValue.charAt(selection) === "'") {
                    newInputValue = beforeSelection + newValue.substring(selection + 1 - wordStart) + afterSelection;
                    inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
                }
                else {
                    newInputValue = `${beforeSelection + newValue.substring(selection - wordStart)} ${afterSelection}`;
                    inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
                }
            }
            else {
                newInputValue = `${beforeSelection + newValue.substring(selection - wordStart) + afterSelection.trimEnd()} `;
                inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
            }
        }
        else {
            newInputValue = `${inputValue.trim()} ${newValue.trim()} `;
        }
        setValue({ value: newInputValue, name: newInputValue });
        setInputValue(newInputValue);
        setIsSelected(true);
    };
    const location = inputRef.current?.selectionStart;
    const letterWidth = 5;
    const renderSuggestions = () => {
        const style = {
            margin: '0.125rem 0',
            left: `${location * letterWidth + 230}px`,
            position: 'absolute'
        };
        if (options.length) {
            return (React.createElement(List, { isOpen: true, style: style, "data-testid": "suggestions-div" },
                React.createElement("div", { className: "tw-box-border tw-overflow-auto tw-scrollbar-default dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark tw-overflow-x-hidden tw-bg-white dark:tw-bg-black" },
                    React.createElement("div", { className: "tw-box-border tw-relative" }, options.map((suggestion, index) => {
                        return (React.createElement(ListItem, { key: `${index}-suggestion-item`, onClick: onSuggestionClick, "data-testid": `${index}-suggestion-item` }, suggestion.name));
                    })))));
        }
        return (React.createElement(List, { isOpen: true, style: style, "data-testid": "suggestions-not-found-div" },
            React.createElement(ListItem, null,
                React.createElement("em", null, "Not found"))));
    };
    const borderColor = errors.length
        ? 'tw-border-red-500 dark:tw-border-red-500'
        : props.disabled
            ? 'tw-border-gray-300 dark:tw-border-gray-900'
            : 'tw-border-gray-300 dark:tw-border-gray-900';
    const rootClassName = classNames('tw-border-solid tw-flex tw-items-center tw-space-x-2 tw-px-2 tw-py-1 tw-h-8 tw-border tw-rounded tw-box-border', borderColor, 'tw-bg-white focus-within:tw-custom-ring dark:tw-bg-black tw-bg-opacity-80 dark:tw-bg-opacity-[0.16]', props.disabled && 'tw-text-disabled tw-cursor-not-allowed');
    const inputClassName = classNames('tw-font-lato tw-border-0 focus:tw-outline-none tw-placeholder-gray-300 dark:tw-placeholder-gray-400 tw-text-default dark:tw-text-gray-100 tw-flex-auto tw-bg-transparent tw-text-xs', props.disabled && 'tw-cursor-not-allowed');
    const searchIcon = (React.createElement("span", { className: "p-0.5", style: { marginRight: '-0.125rem' } }, !errors.length ? (React.createElement(SearchIcon, { size: "md", className: "tw-text-gray-600 dark:tw-text-gray-400 tw-mt-1.5", "data-testid": "search-icon" })) : (React.createElement(Tooltip, { label: errors[0].message, singleLine: true, defaultPlacement: 'bottom-start' },
        React.createElement(ExclamationCircleIcon, { size: "xs", className: "tw-text-red-500 dark:tw-text-red-400", "data-testid": "error-icon" })))));
    const clearButton = (React.createElement("span", { className: "p-0.5", style: { marginRight: '-0.125rem' }, onClick: handleClickClear },
        React.createElement(CloseIcon, { size: "xs", className: "tw-text-gray-600 dark:tw-text-gray-400 tw-cursor-pointer", "data-testid": "close-icon" })));
    const copyButton = (React.createElement("span", { onClick: handleClickCopy },
        React.createElement(CopyIcon, { size: "md", className: "tw-text-gray-600 dark:tw-text-gray-400 tw-cursor-pointer", "data-testid": "copy-icon" })));
    const searchButton = (React.createElement(Button, { appearance: "primary", onClick: () => onQuerySubmit(), addClassName: "tw-h-5", "data-testid": "search-button" }, "Search"));
    return (React.createElement("div", { className: "tw-w-full" },
        React.createElement("span", { className: rootClassName, "data-testid": "input-wrapper" },
            searchIcon,
            React.createElement("input", { disabled: props.disabled, onClick: handleCursorMayChange, onChange: handleInputChange, onKeyUp: handleCursorMayChange, onKeyDown: handleOnKeyDown, placeholder: 'Log query placeholder', className: inputClassName, ref: inputRef, value: inputValue, role: "textbox", "data-testid": "log-query-textbox" }),
            clearButton,
            copyButton,
            searchButton),
        open && (React.createElement(Portal, null,
            React.createElement("div", { className: "tw-z-30", ref: dropdownRef }, renderSuggestions())))));
}
//# sourceMappingURL=LogQuery.js.map